import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { Field, SelectField, SecondaryButton, StepAlert } from "../ui";
import { SHAPE } from "../capacity";
import { resolveTankSizing } from "../tankSizing";

const shapes = [
  [SHAPE.RECTANGLE, "Rectangular"],
  [SHAPE.SQUARE, "Square footprint"],
  [SHAPE.CYLINDER_VERTICAL, "Round, upright"],
  [SHAPE.CYLINDER_HORIZONTAL, "Round, on its side"],
];

const FIELD_LABELS = {
  length_in: "Length",
  width_in: "Width",
  depth_in: "Inside depth",
  diameter_in: "Diameter",
  capacity_gallons: "Full capacity",
};

export default function TankSizeHelper({ value, accepted, dispatch, t, darkMode }) {
  const result = resolveTankSizing(value);
  const geometry = result.geometry;
  const estimate = value.method === "estimate";
  const horizontal = value.shape === SHAPE.CYLINDER_HORIZONTAL;
  const vertical = value.shape === SHAPE.CYLINDER_VERTICAL;
  const set = (patch) => dispatch({
    type: "SET_TANK_SIZING",
    value: { ...value, ...patch },
  });
  const fields = estimate
    ? horizontal ? [["diameter_in", "Inside diameter / full height (in)"]]
      : [["depth_in", "Inside depth / height (in)"]]
    : [
      ...(!vertical ? [["length_in", "Inside length (in)"]] : []),
      ...(value.shape === SHAPE.RECTANGLE ? [["width_in", "Inside width (in)"]] : []),
      ...(vertical || horizontal ? [["diameter_in", "Inside diameter (in)"]] : []),
      ...(!horizontal ? [["depth_in", "Inside depth / height (in)"]] : []),
    ];
  return (
    <Box component="details" my={2} sx={{ color: t.textStrong }}>
      <Box component="summary" sx={{ cursor: "pointer", py: 1, fontWeight: 700 }}>
        Help me work out the tank size
      </Box>
      <Box display="grid" gap={2} mt={2}>
        <SelectField label="Tank shape" value={value.shape}
          onChange={(event) => set({ shape: event.target.value })} t={t} darkMode={darkMode}>
          {shapes.map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}
        </SelectField>
        <SelectField label="What information do you have?" value={value.method}
          onChange={(event) => set({ method: event.target.value })} t={t} darkMode={darkMode}>
          <MenuItem value="measurements">Tank measurements - calculate gallons</MenuItem>
          <MenuItem value="rated">Measurements + rated gallons</MenuItem>
          <MenuItem value="estimate">Rated gallons + depth/diameter - estimate size</MenuItem>
        </SelectField>
        {value.method !== "measurements" ? (
          <Field label="Rated FULL capacity (US gallons)" type="number"
            value={value.capacity_gallons}
            onChange={(event) => set({ capacity_gallons: event.target.value })}
            inputProps={{ min: 0, step: "any" }} t={t} darkMode={darkMode}
            helperText="Full tank capacity, not current liquid gallons or a working fill limit." />
        ) : null}
        {fields.map(([key, label]) => (
          <Field key={key} label={label} type="number" value={value.dims[key]}
            onChange={(event) => set({ dims: { ...value.dims, [key]: event.target.value } })}
            inputProps={{ min: 0, step: "any" }} t={t} darkMode={darkMode} />
        ))}
        {estimate && value.shape === SHAPE.RECTANGLE ? (
          <Field label="Length compared with width (optional)" type="number" value={value.ratio}
            onChange={(event) => set({ ratio: event.target.value })}
            inputProps={{ min: 0, step: "any" }} t={t} darkMode={darkMode}
            helperText="Enter 2 if twice as long as wide. Blank assumes equal sides." />
        ) : null}
        {geometry ? (
          <Box role="status" aria-live="polite">
            {Object.entries(geometry.values).map(([key, number]) => (
              <Typography key={key} variant="body2">
                {FIELD_LABELS[key]}: {number.toFixed(2)} {key === "capacity_gallons" ? "US gal" : "in"}
              </Typography>
            ))}
            <Typography variant="caption">
              Dimensions: {geometry.dimensionSource}. Capacity: {geometry.capacitySource}.
            </Typography>
            {geometry.assumedEqualSides ? (
              <StepAlert severity="warning">Equal sides are assumed, not measured.</StepAlert>
            ) : null}
            {value.method === "rated" ? (
              <StepAlert severity="info">
                Dimensions calculate to {result.geometricGallons.toFixed(2)} US gal.
                The entered rated capacity is retained; check any discrepancy.
              </StepAlert>
            ) : null}
          </Box>
        ) : (
          <StepAlert severity="info">{Object.values(result.errors).join(" ")}</StepAlert>
        )}
        <SecondaryButton t={t} disabled={!geometry}
          onClick={() => dispatch({ type: "ACCEPT_TANK_GEOMETRY", geometry })}>
          Use this tank size for this installation
        </SecondaryButton>
        {accepted ? (
          <StepAlert severity="success">
            Tank size selected for review and saving with this installation.
          </StepAlert>
        ) : null}
        <SecondaryButton t={t}
          onClick={() => dispatch({ type: "CLEAR_TANK_GEOMETRY" })}>
          Do not change the tank size
        </SecondaryButton>
        <StepAlert severity="warning">
          Estimates do not locate the inlet or sensor face. Use inside dimensions
          from records or the manufacturer; do not enter or reach into a tank to measure.
        </StepAlert>
      </Box>
    </Box>
  );
}
